import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { supabase, IS_TEST } from "../config";
import { uploadPhotoToGDrive } from "../utils/uploadToGDrive";
import { POSTER_THEMES, DEFAULT_POSTER_THEME_ID, getPosterTheme } from "../constants/posterThemes";
import {
  ADMIN_PASSWORD,
  isAdminAuthenticated,
  setAdminAuthenticated,
} from "../utils/adminAuth";

// Convert any Date / ISO timestamp to "YYYY-MM-DDTHH:mm" in America/Chicago
// for use as a value in <input type="datetime-local">. Handles DST correctly.
const utcToCentralLocalInput = (input) => {
  if (!input) return "";
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value;
  let hour = get("hour");
  if (hour === "24") hour = "00";
  return `${get("year")}-${get("month")}-${get("day")}T${hour}:${get("minute")}`;
};

// Detect whether a "YYYY-MM-DDTHH:mm" wall-clock time in America/Chicago
// falls in CDT or CST and append the right offset for storage as UTC.
const appendCentralOffset = (dt) => {
  if (!dt) return null;
  // Probe the timezone abbreviation for that instant.
  const probe = new Date(dt + "Z"); // arbitrary; we only need the date for tz lookup
  const tzName = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    timeZoneName: "short",
  })
    .formatToParts(probe)
    .find((p) => p.type === "timeZoneName")?.value;
  return dt + (tzName === "CDT" ? "-05:00" : "-06:00");
};

// Deployed Lambda behind API Gateway. Fast calls only (<30s cap):
//   /status, /schedule-youtube, /generate-poster (reset).
// Long-running poster generation uses the async Supabase poster_jobs
// queue (see handleGeneratePoster below).
const LAMBDA_API_URL = "https://8pxejclxy5.execute-api.us-east-1.amazonaws.com";

const EpisodeCreator = () => {
  // Auth
  const [isAuthenticated, setIsAuthenticated] = useState(() => isAdminAuthenticated());
  const [password, setPassword] = useState("");

  // Main tabs
  const [activeTab, setActiveTab] = useState("create");

  // Episode form
  const [episodeForm, setEpisodeForm] = useState({
    title: "",
    episode_type: "weekly",
    scheduled_start: "",
    scheduled_end: "",
  });
  const [editingEpisodeId, setEditingEpisodeId] = useState(null);

  // Storyteller picker
  const [allStorytellers, setAllStorytellers] = useState([]);
  const [storytellerSearch, setStorytellerSearch] = useState("");
  const [selectedStorytellers, setSelectedStorytellers] = useState([]); // [{storyteller_id, name, photo_url, sort_order}]
  const [hostStorytellerId, setHostStorytellerId] = useState("");

  // Inline add storyteller
  const [showAddNew, setShowAddNew] = useState(false);
  const [newStoryteller, setNewStoryteller] = useState({
    storyteller_telugu: "",
    storyteller_english: "",
    gender: "Male",
    introduction: "",
  });
  const [newPhotoFile, setNewPhotoFile] = useState(null);
  const [newPhotoPreview, setNewPhotoPreview] = useState(null);
  const [isAddingStoryteller, setIsAddingStoryteller] = useState(false);

  // Inline edit storyteller
  const [editingStorytellerId, setEditingStorytellerId] = useState(null);
  const [editStoryteller, setEditStoryteller] = useState({
    storyteller_telugu: "",
    storyteller_english: "",
    gender: "Male",
    introduction: "",
  });
  const [editPhotoFile, setEditPhotoFile] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState(null);
  const [isSavingStoryteller, setIsSavingStoryteller] = useState(false);

  // Photo upload for existing storyteller
  const [uploadingPhotoFor, setUploadingPhotoFor] = useState(null);

  // Poster — Google Slides deck
  const [slidesDeckId, setSlidesDeckId] = useState("");  // will be stored in poster_slide_id
  const [isGeneratingPoster, setIsGeneratingPoster] = useState(false);
  const [posterServerOnline, setPosterServerOnline] = useState(null); // null=checking, true, false
  // Poster theme — transient selection used when Generate Poster is clicked
  const [posterThemeId, setPosterThemeId] = useState(DEFAULT_POSTER_THEME_ID);
  // YouTube scheduling
  const [isSchedulingYouTube, setIsSchedulingYouTube] = useState(false);
  // Last poster generation result, shown inline next to the button
  const [lastPosterResult, setLastPosterResult] = useState(null);
  // The job_id of the currently-running poster generation (if any).
  // Held in a ref so the safety-net setTimeout and polling fallback
  // can read the live value without stale-closure issues.
  const activePosterJobRef = useRef(null);

  // Episodes list
  const [episodes, setEpisodes] = useState([]);

  // UI
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // ---------- Auth ----------
  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setAdminAuthenticated(true);
    } else {
      setErrorMsg("Incorrect password.");
    }
  };

  // ---------- Fetch all storytellers ----------
  const fetchStorytellers = useCallback(async () => {
    const { data } = await supabase
      .from("storytellers")
      .select("storyteller_id, storyteller_telugu, storyteller_english, photo_url, gender, is_host, introduction")
      .order("storyteller_telugu", { ascending: true });
    setAllStorytellers(data || []);
  }, []);

  // (poster templates removed — using Google Slides approach)

  // ---------- Fetch episodes list ----------
  // Show upcoming episodes first (next → later dates), then past episodes
  // most-recent-first. Episodes without a scheduled time go last, newest
  // episode_id first so drafts aren't silently hidden.
  const fetchEpisodes = useCallback(async () => {
    const nowIso = new Date().toISOString();
    const [upcomingRes, pastRes, undatedRes] = await Promise.all([
      supabase
        .from("episodes")
        .select("*")
        .gte("scheduled_start", nowIso)
        .order("scheduled_start", { ascending: true })
        .limit(60),
      supabase
        .from("episodes")
        .select("*")
        .lt("scheduled_start", nowIso)
        .order("scheduled_start", { ascending: false })
        .limit(30),
      supabase
        .from("episodes")
        .select("*")
        .is("scheduled_start", null)
        .order("episode_id", { ascending: false })
        .limit(10),
    ]);
    setEpisodes([
      ...(upcomingRes.data || []),
      ...(pastRes.data || []),
      ...(undatedRes.data || []),
    ]);
  }, []);

  // ---------- Init ----------
  useEffect(() => {
    if (isAuthenticated) {
      fetchStorytellers();
      fetchEpisodes();
    }
  }, [isAuthenticated, fetchStorytellers, fetchEpisodes]);

  // ---------- Check poster server status ----------
  useEffect(() => {
    if (!isAuthenticated) return;
    fetch(`${LAMBDA_API_URL}/status`)
      .then((r) => r.json())
      .then(() => setPosterServerOnline(true))
      .catch(() => setPosterServerOnline(false));
  }, [isAuthenticated]);

  // ---------- Generate poster (async, event-driven) ----------
  // INSERT a row into poster_jobs; Supabase Database Webhook fires
  // on INSERT and calls the Lambda's /process-poster-job. The Lambda
  // runs Gemini → Drive → Slides → populate (~40s) and PATCHes the
  // same row back. We subscribe to that row via Supabase Realtime —
  // no polling, no timers, no stuck state.
  //
  // Reset is fast (<10s) so it stays a sync Lambda call.
  const handleGeneratePoster = async (reset = false) => {
    if (!editingEpisodeId) {
      setErrorMsg("Save the episode first, then generate poster.");
      return;
    }
    if (!slidesDeckId) {
      setErrorMsg("Enter a Google Slides deck ID first.");
      return;
    }

    setErrorMsg("");
    setLastPosterResult(null);

    if (reset) {
      // Sync path — quick Lambda call through API Gateway.
      setIsGeneratingPoster(true);
      try {
        const resp = await fetch(`${LAMBDA_API_URL}/generate-poster`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            episode_id: editingEpisodeId,
            deck_id: slidesDeckId,
            env: IS_TEST ? "test" : "prod",
            reset: true,
          }),
        });
        const result = await resp.json();
        if (result.success) {
          setSuccessMsg(result.message || "Slide reset.");
          setTimeout(() => setSuccessMsg(""), 6000);
        } else {
          setErrorMsg("Reset failed: " + (result.error || "Unknown error"));
        }
      } catch (err) {
        setErrorMsg("Cannot reach Lambda: " + err.message);
      } finally {
        setIsGeneratingPoster(false);
      }
      return;
    }

    // Async path — enqueue + subscribe + poll (belt-and-braces).
    setIsGeneratingPoster(true);
    try {
      const { data, error } = await supabase
        .from("poster_jobs")
        .insert({
          episode_id: editingEpisodeId,
          theme_id: posterThemeId,
          deck_id: slidesDeckId,
          env: IS_TEST ? "test" : "prod",
          requested_by: "admin",
          status: "pending",
        })
        .select()
        .single();
      if (error) throw error;

      const jobId = data.job_id;
      activePosterJobRef.current = jobId;

      // Single point where a terminal job state is processed. Called
      // from the Realtime UPDATE listener, the immediate re-check, and
      // the polling fallback — whichever wins the race.
      let resolved = false;
      let channel = null;
      let pollInterval = null;
      let safetyTimer = null;

      const cleanup = () => {
        if (channel) {
          supabase.removeChannel(channel);
          channel = null;
        }
        if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
        if (safetyTimer) {
          clearTimeout(safetyTimer);
          safetyTimer = null;
        }
        activePosterJobRef.current = null;
      };

      const handleRow = (row) => {
        if (!row || resolved) return;
        if (row.status === "done") {
          resolved = true;
          setLastPosterResult({
            success: true,
            message: row.result?.message || "Poster generated.",
            deckUrl: row.result?.deckUrl,
            result: row.result,
          });
          setSuccessMsg(row.result?.message || "Poster generated.");
          setTimeout(() => setSuccessMsg(""), 10000);
          if (row.result?.deckUrl) window.open(row.result.deckUrl, "_blank");
          setIsGeneratingPoster(false);
          fetchEpisodes();
          cleanup();
        } else if (row.status === "failed" || row.status === "canceled") {
          resolved = true;
          setErrorMsg(
            `Poster job ${row.status}: ${row.error_message || "(no detail)"}`
          );
          setIsGeneratingPoster(false);
          cleanup();
        }
        // 'pending' / 'running' → keep spinner up; do nothing
      };

      // 1) Realtime subscription — fastest path when it works.
      channel = supabase
        .channel(`poster_job_${jobId}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "poster_jobs",
            filter: `job_id=eq.${jobId}`,
          },
          (payload) => handleRow(payload.new)
        )
        .subscribe(async (status) => {
          // 2) The moment the subscription is live, re-read the row.
          // The Lambda can finish faster than the SUBSCRIBE handshake,
          // and any UPDATEs emitted before SUBSCRIBED is acknowledged
          // are lost. Re-querying here catches that race.
          if (status === "SUBSCRIBED") {
            const { data: row } = await supabase
              .from("poster_jobs")
              .select("status, result, error_message")
              .eq("job_id", jobId)
              .single();
            handleRow(row);
          }
        });

      // 3) Polling fallback — covers the case where Realtime is down,
      // misconfigured for this table, or a transient socket drop. Cheap
      // (1 row, indexed PK) and stops as soon as the job resolves.
      pollInterval = setInterval(async () => {
        if (resolved) return;
        const { data: row } = await supabase
          .from("poster_jobs")
          .select("status, result, error_message")
          .eq("job_id", jobId)
          .single();
        handleRow(row);
      }, 5000);

      // 4) Safety net: 3 min without resolution → surface a clear
      // diagnostic instead of leaving the user stuck. Uses the ref so
      // we don't rely on a captured-by-value isGeneratingPoster.
      safetyTimer = setTimeout(() => {
        if (resolved) return;
        if (activePosterJobRef.current !== jobId) return;
        setErrorMsg(
          "No update from the worker in 3 minutes. " +
            "Click 'Check now' or look at the poster_jobs row directly."
        );
        setIsGeneratingPoster(false);
        cleanup();
      }, 180000);
    } catch (err) {
      setErrorMsg("Failed to queue poster job: " + (err.message || err));
      setIsGeneratingPoster(false);
    }
  };

  // Manual "Check now" — re-reads the active poster job's status from
  // Supabase. Useful when the page got wedged on "generating" because
  // both Realtime and the poll missed a fast Lambda finish.
  const handleCheckPosterJobNow = async () => {
    const jobId = activePosterJobRef.current;
    if (!jobId) {
      // No active job tracked in this session — fall back to the
      // newest pending/running job for this episode, if any.
      if (!editingEpisodeId) return;
      const { data } = await supabase
        .from("poster_jobs")
        .select("job_id, status, result, error_message")
        .eq("episode_id", editingEpisodeId)
        .order("created_at", { ascending: false })
        .limit(1);
      const row = data?.[0];
      if (!row) {
        setErrorMsg("No poster job found for this episode.");
        return;
      }
      if (row.status === "done") {
        setLastPosterResult({
          success: true,
          message: row.result?.message || "Poster generated.",
          deckUrl: row.result?.deckUrl,
          result: row.result,
        });
        setSuccessMsg(row.result?.message || "Poster ready.");
        if (row.result?.deckUrl) window.open(row.result.deckUrl, "_blank");
        setIsGeneratingPoster(false);
        fetchEpisodes();
      } else if (row.status === "failed" || row.status === "canceled") {
        setErrorMsg(
          `Last poster job ${row.status}: ${row.error_message || "(no detail)"}`
        );
        setIsGeneratingPoster(false);
      } else {
        setSuccessMsg(`Last poster job is ${row.status}.`);
      }
      return;
    }
    const { data: row } = await supabase
      .from("poster_jobs")
      .select("status, result, error_message")
      .eq("job_id", jobId)
      .single();
    if (!row) {
      setErrorMsg("Could not load job row.");
      return;
    }
    if (row.status === "done") {
      setLastPosterResult({
        success: true,
        message: row.result?.message || "Poster generated.",
        deckUrl: row.result?.deckUrl,
        result: row.result,
      });
      setSuccessMsg(row.result?.message || "Poster generated.");
      if (row.result?.deckUrl) window.open(row.result.deckUrl, "_blank");
      setIsGeneratingPoster(false);
      activePosterJobRef.current = null;
      fetchEpisodes();
    } else if (row.status === "failed" || row.status === "canceled") {
      setErrorMsg(
        `Poster job ${row.status}: ${row.error_message || "(no detail)"}`
      );
      setIsGeneratingPoster(false);
      activePosterJobRef.current = null;
    } else {
      setSuccessMsg(`Still ${row.status}…`);
    }
  };

  // ---------- Schedule on YouTube ----------
  const handleScheduleYouTube = async () => {
    if (!editingEpisodeId) {
      setErrorMsg("Save the episode first, then schedule YouTube.");
      return;
    }
    if (!window.confirm(
      "Create a YouTube Live broadcast for this episode? " +
      "Thumbnail is pulled from slide 1 of the deck, and the video is added to the KathaKala playlist."
    )) return;

    setIsSchedulingYouTube(true);
    setErrorMsg("");

    try {
      const resp = await fetch(`${LAMBDA_API_URL}/schedule-youtube`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          episode_id: editingEpisodeId,
          env: IS_TEST ? "test" : "prod",
        }),
      });
      const result = await resp.json();
      if (result.success) {
        setSuccessMsg(result.message || "YouTube broadcast scheduled.");
        setTimeout(() => setSuccessMsg(""), 10000);
        if (result.youtubeUrl) window.open(result.youtubeUrl, "_blank");
        await fetchEpisodes();
      } else {
        setErrorMsg("YouTube scheduling failed: " + (result.error || "Unknown error"));
      }
    } catch (err) {
      setErrorMsg("Cannot reach Lambda: " + err.message);
    } finally {
      setIsSchedulingYouTube(false);
    }
  };

  // ---------- Storyteller search filter ----------
  const filteredStorytellers = useMemo(() => {
    if (!storytellerSearch.trim()) return allStorytellers;
    const q = storytellerSearch.toLowerCase();
    return allStorytellers.filter(
      (s) =>
        (s.storyteller_telugu || "").includes(storytellerSearch) ||
        (s.storyteller_english || "").toLowerCase().includes(q)
    );
  }, [allStorytellers, storytellerSearch]);

  // ---------- Storyteller display-name helpers ----------
  // We standardised on storing names as "LastName GivenName(s)" in both
  // Telugu and English, so the first word of storyteller_english is the
  // last name we want in episode titles. The DB still has legacy rows
  // that prefix honorifics like "Sri.", "Smt." etc — skip those when
  // picking the display word.
  //
  // Episode titles are English-only — if a storyteller has no English
  // name on record, we return empty for that slot rather than fall
  // back to Telugu (so titles never end up mixed-script). The caller
  // filters empties out.
  const HONORIFIC_RE =
    /^(sri|shri|smt|srimati|dr|mr|mrs|ms|g[aā]ru|gaaru)\.?$/i;

  const lastNameFromEnglish = useCallback((full) => {
    const words = (full?.storyteller_english || "")
      .trim()
      .split(/\s+/)
      .filter((w) => w && !HONORIFIC_RE.test(w));
    return words[0] || "";
  }, [HONORIFIC_RE]);

  // Build the canonical episode title from the currently-selected
  // storytellers: "Kathakala | Last1 | Last2 | Last3". English last
  // names only; storytellers without an English name are skipped.
  // Used by the "Regenerate Title" button and as the auto-fill on
  // save when the title is blank.
  const buildTitleFromStorytellers = useCallback((storytellers) => {
    const lastNames = storytellers
      .map((st) => {
        const full = allStorytellers.find(
          (a) => a.storyteller_id === st.storyteller_id
        );
        return lastNameFromEnglish(full);
      })
      .filter(Boolean);
    if (lastNames.length === 0) return "";
    return `Kathakala | ${lastNames.join(" | ")}`;
  }, [allStorytellers, lastNameFromEnglish]);

  // ---------- Substitute {Storyteller-N} placeholders in the title
  // with the real names of the currently-assigned storytellers. Picks
  // the first non-honorific word of storyteller_english (DB sometimes
  // stores names like "Sri. Venu Orugani" or "Smt. Ganti Ushabala
  // gAru" — the honorific would otherwise leak into the title); falls
  // back to the Telugu name if no English name is set. Untouched
  // placeholders (where no storyteller is in that slot yet) are left
  // in place.
  const applyStorytellerPlaceholders = useCallback(
    (title, storytellers) => {
      if (!title) return title;
      let out = title;
      for (let i = 0; i < 3; i++) {
        const ph = `{Storyteller-${i + 1}}`;
        if (!out.includes(ph)) continue;
        const st = storytellers[i];
        if (!st) continue;
        const full = allStorytellers.find(
          (a) => a.storyteller_id === st.storyteller_id
        );
        const replacement = lastNameFromEnglish(full);
        // Skip substitution if the storyteller has no English name on
        // record — leaves the placeholder visible so the user knows to
        // fix the storyteller record rather than silently emitting
        // Telugu into an English title.
        if (!replacement) continue;
        out = out.split(ph).join(replacement);
      }
      return out;
    },
    [allStorytellers, lastNameFromEnglish]
  );

  // Title resolution happens on Save (and via the Regenerate button) —
  // NOT live as storytellers are added/reordered. Resolving on every
  // selection change would bake stale storyteller_english values into
  // the title when the user is still editing those records.

  // ---------- Select/deselect storyteller ----------
  const toggleStoryteller = (st) => {
    const exists = selectedStorytellers.find((s) => s.storyteller_id === st.storyteller_id);
    if (exists) {
      setSelectedStorytellers((prev) => prev.filter((s) => s.storyteller_id !== st.storyteller_id));
      if (hostStorytellerId === st.storyteller_id) setHostStorytellerId("");
    } else {
      setSelectedStorytellers((prev) => [
        ...prev,
        {
          storyteller_id: st.storyteller_id,
          name: st.storyteller_telugu || st.storyteller_english,
          photo_url: st.photo_url,
          sort_order: prev.length,
        },
      ]);
    }
  };

  // ---------- Reorder storytellers ----------
  const moveStoryteller = (idx, direction) => {
    const newList = [...selectedStorytellers];
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= newList.length) return;
    [newList[idx], newList[swapIdx]] = [newList[swapIdx], newList[idx]];
    newList.forEach((s, i) => (s.sort_order = i));
    setSelectedStorytellers(newList);
  };

  // ---------- Add new storyteller inline ----------
  const handleAddNewStoryteller = async () => {
    if (!newStoryteller.storyteller_telugu.trim()) {
      setErrorMsg("Telugu name is required.");
      return;
    }
    setIsAddingStoryteller(true);
    setErrorMsg("");

    try {
      let photoUrl = null;
      if (newPhotoFile) {
        photoUrl = await uploadPhotoToGDrive(
          newPhotoFile,
          "storyteller",
          newStoryteller.storyteller_english || newStoryteller.storyteller_telugu
        );
      }

      const { data, error } = await supabase
        .from("storytellers")
        .insert({
          storyteller_telugu: newStoryteller.storyteller_telugu.trim(),
          storyteller_english: newStoryteller.storyteller_english.trim() || null,
          gender: newStoryteller.gender,
          introduction: newStoryteller.introduction.trim() || null,
          photo_url: photoUrl,
        })
        .select()
        .single();

      if (error) throw error;

      // Add to selected list
      setSelectedStorytellers((prev) => [
        ...prev,
        {
          storyteller_id: data.storyteller_id,
          name: data.storyteller_telugu,
          photo_url: data.photo_url,
          sort_order: prev.length,
        },
      ]);

      // Refresh and reset
      await fetchStorytellers();
      setNewStoryteller({ storyteller_telugu: "", storyteller_english: "", gender: "Male", introduction: "" });
      setNewPhotoFile(null);
      setNewPhotoPreview(null);
      setShowAddNew(false);
      setSuccessMsg("Storyteller added!");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setErrorMsg("Failed to add storyteller: " + err.message);
    } finally {
      setIsAddingStoryteller(false);
    }
  };

  // ---------- Upload photo for existing storyteller ----------
  const handleUploadExistingPhoto = async (storytellerId, file) => {
    setUploadingPhotoFor(storytellerId);
    try {
      const st = allStorytellers.find((s) => s.storyteller_id === storytellerId);
      const photoUrl = await uploadPhotoToGDrive(
        file,
        "storyteller",
        st?.storyteller_english || st?.storyteller_telugu || "unknown"
      );

      await supabase
        .from("storytellers")
        .update({ photo_url: photoUrl })
        .eq("storyteller_id", storytellerId);

      // Update local state
      setAllStorytellers((prev) =>
        prev.map((s) => (s.storyteller_id === storytellerId ? { ...s, photo_url: photoUrl } : s))
      );
      setSelectedStorytellers((prev) =>
        prev.map((s) => (s.storyteller_id === storytellerId ? { ...s, photo_url: photoUrl } : s))
      );
      setSuccessMsg("Photo uploaded!");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setErrorMsg("Photo upload failed: " + err.message);
    } finally {
      setUploadingPhotoFor(null);
    }
  };

  // ---------- Edit storyteller inline ----------
  const handleOpenEditStoryteller = (storytellerId) => {
    const st = allStorytellers.find((s) => s.storyteller_id === storytellerId);
    if (!st) return;
    setEditingStorytellerId(storytellerId);
    setEditStoryteller({
      storyteller_telugu: st.storyteller_telugu || "",
      storyteller_english: st.storyteller_english || "",
      gender: st.gender || "Male",
      introduction: st.introduction || "",
    });
    setEditPhotoFile(null);
    setEditPhotoPreview(st.photo_url || null);
  };

  const handleSaveEditStoryteller = async () => {
    if (!editingStorytellerId) return;
    setIsSavingStoryteller(true);
    setErrorMsg("");

    try {
      const updates = {
        storyteller_telugu: editStoryteller.storyteller_telugu.trim(),
        storyteller_english: editStoryteller.storyteller_english.trim() || null,
        gender: editStoryteller.gender,
        introduction: editStoryteller.introduction.trim() || null,
      };

      // Upload new photo if selected
      if (editPhotoFile) {
        const photoUrl = await uploadPhotoToGDrive(
          editPhotoFile,
          "storyteller",
          editStoryteller.storyteller_english || editStoryteller.storyteller_telugu
        );
        updates.photo_url = photoUrl;
      }

      const { error } = await supabase
        .from("storytellers")
        .update(updates)
        .eq("storyteller_id", editingStorytellerId);
      if (error) throw error;

      // Update local state
      setAllStorytellers((prev) =>
        prev.map((s) =>
          s.storyteller_id === editingStorytellerId
            ? { ...s, ...updates, photo_url: updates.photo_url || s.photo_url }
            : s
        )
      );
      setSelectedStorytellers((prev) =>
        prev.map((s) =>
          s.storyteller_id === editingStorytellerId
            ? { ...s, name: updates.storyteller_telugu, photo_url: updates.photo_url || s.photo_url }
            : s
        )
      );

      setEditingStorytellerId(null);
      setEditPhotoFile(null);
      setEditPhotoPreview(null);
      setSuccessMsg("Storyteller updated!");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setErrorMsg("Failed to update storyteller: " + err.message);
    } finally {
      setIsSavingStoryteller(false);
    }
  };

  // ---------- Save episode ----------
  const handleSaveEpisode = async () => {
    if (!episodeForm.scheduled_start) {
      setErrorMsg("Scheduled date/time is required.");
      return;
    }
    if (selectedStorytellers.length === 0) {
      setErrorMsg("Select at least one storyteller.");
      return;
    }

    setIsSaving(true);
    setErrorMsg("");

    try {
      // Resolve the title on save (not live during selection) so any
      // last-minute edits to storyteller_english are picked up:
      //   1. If the title is blank → auto-fill the canonical
      //      "Kathakala | Last1 | Last2 | Last3" format.
      //   2. If the title contains {Storyteller-N} placeholders →
      //      substitute the current English last names.
      //   3. Otherwise (concrete custom title) → leave it alone. The
      //      user can click "↻ Regenerate from storytellers" to opt
      //      into a rebuild.
      const trimmedTitle = (episodeForm.title || "").trim();
      let finalTitle;
      if (!trimmedTitle) {
        finalTitle = buildTitleFromStorytellers(selectedStorytellers);
      } else if (/\{Storyteller-\d\}/.test(trimmedTitle)) {
        finalTitle = applyStorytellerPlaceholders(
          trimmedTitle,
          selectedStorytellers
        );
      } else {
        finalTitle = trimmedTitle;
      }
      if (finalTitle !== episodeForm.title) {
        setEpisodeForm((prev) => ({ ...prev, title: finalTitle }));
      }

      // Fields that are safe to write on every save. Status is NOT
      // here — when editing an episode, we don't want to clobber
      // 'youtube_scheduled' / 'poster_generated' / etc. back to
      // 'draft'. That bug bit ep 354 on 2026-05-23: scheduling worked,
      // but a subsequent Save reset status='draft' and masked it.
      const episodeData = {
        title: finalTitle || null,
        episode_type: episodeForm.episode_type,
        scheduled_start: appendCentralOffset(episodeForm.scheduled_start),
        scheduled_end: appendCentralOffset(episodeForm.scheduled_end),
        host_storyteller_id: hostStorytellerId || null,
        poster_slide_id: slidesDeckId || null,
        publish_date: episodeForm.scheduled_start ? episodeForm.scheduled_start.split("T")[0] : null,
      };

      let episodeId;

      if (editingEpisodeId) {
        // Update path — preserve whatever status the row already has.
        const { error } = await supabase
          .from("episodes")
          .update(episodeData)
          .eq("episode_id", editingEpisodeId);
        if (error) throw error;
        episodeId = editingEpisodeId;

        // Clear existing storyteller assignments
        await supabase
          .from("episode_storytellers")
          .delete()
          .eq("episode_id", episodeId);
      } else {
        // Insert path — new episode starts at status 'draft'. (DB
        // column has 'draft' as default, but set explicitly for
        // clarity. Subsequent updates leave status alone — see above.)
        const { data, error } = await supabase
          .from("episodes")
          .insert({ ...episodeData, status: "draft" })
          .select()
          .single();
        if (error) throw error;
        episodeId = data.episode_id;
      }

      // Insert storyteller assignments
      const assignments = selectedStorytellers.map((s) => ({
        episode_id: episodeId,
        storyteller_id: s.storyteller_id,
        sort_order: s.sort_order,
      }));

      if (assignments.length > 0) {
        const { error: asError } = await supabase
          .from("episode_storytellers")
          .insert(assignments);
        if (asError) throw asError;
      }

      setSuccessMsg(editingEpisodeId ? "Episode updated!" : "Episode created!");
      setTimeout(() => setSuccessMsg(""), 3000);
      resetForm();
      await fetchEpisodes();
    } catch (err) {
      setErrorMsg("Failed to save: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // ---------- Load episode for editing ----------
  const handleEditEpisode = async (episode) => {
    setEditingEpisodeId(episode.episode_id);
    setEpisodeForm({
      title: episode.title || "",
      episode_type: episode.episode_type || "weekly",
      scheduled_start: utcToCentralLocalInput(episode.scheduled_start),
      scheduled_end: utcToCentralLocalInput(episode.scheduled_end),
    });
    setHostStorytellerId(episode.host_storyteller_id || "");
    setSlidesDeckId(episode.poster_slide_id || "");

    // Load assigned storytellers
    const { data } = await supabase
      .from("episode_storytellers")
      .select("storyteller_id, sort_order")
      .eq("episode_id", episode.episode_id)
      .order("sort_order", { ascending: true });

    if (data) {
      const selected = data.map((a) => {
        const st = allStorytellers.find((s) => s.storyteller_id === a.storyteller_id);
        return {
          storyteller_id: a.storyteller_id,
          name: st?.storyteller_telugu || st?.storyteller_english || "Unknown",
          photo_url: st?.photo_url,
          sort_order: a.sort_order,
        };
      });
      setSelectedStorytellers(selected);
    }

    setActiveTab("create");
    window.scrollTo(0, 0);
  };

  // ---------- Reset form ----------
  const resetForm = () => {
    setEditingEpisodeId(null);
    setEpisodeForm({ title: "", episode_type: "weekly", scheduled_start: "", scheduled_end: "" });
    setSelectedStorytellers([]);
    setHostStorytellerId("");
    setStorytellerSearch("");
    // Keep slidesDeckId since it's typically the same deck each week
  };

  // ---------- Render: Login ----------
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            Kathakala Episode Creator
          </h1>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            <button
              type="submit"
              className="w-full bg-green-700 text-white py-3 rounded-lg hover:bg-green-800 font-semibold"
            >
              Login
            </button>
            {errorMsg && <p className="text-red-600 text-sm mt-2">{errorMsg}</p>}
          </form>
        </div>
      </div>
    );
  }

  // ---------- Render: Main ----------
  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold text-green-800 text-center mb-2">
          Kathakala Episode Creator
        </h1>

        {/* Messages */}
        {errorMsg && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded mb-3 text-sm">
            {errorMsg}
            <button onClick={() => setErrorMsg("")} className="float-right font-bold">✕</button>
          </div>
        )}
        {successMsg && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded mb-3 text-sm">
            {successMsg}
          </div>
        )}

        {/* Tabs */}
        <div className="flex justify-center gap-1 mb-6 bg-white rounded-lg shadow-sm p-1 max-w-md mx-auto">
          {[
            { key: "create", label: editingEpisodeId ? "Edit Episode" : "Create Episode" },
            { key: "episodes", label: "Episodes" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition flex-1 ${
                activeTab === tab.key
                  ? "bg-green-700 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ==================== CREATE/EDIT TAB ==================== */}
        {activeTab === "create" && (
          <div className="space-y-6">

            {/* ---- Episode Details ---- */}
            <div className="bg-white rounded-lg shadow-sm p-5">
              <h2 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">
                Episode Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">Title (optional)</label>
                    <button
                      type="button"
                      onClick={() => {
                        const t = buildTitleFromStorytellers(selectedStorytellers);
                        if (t) setEpisodeForm({ ...episodeForm, title: t });
                      }}
                      disabled={selectedStorytellers.length === 0}
                      className="text-xs text-green-700 hover:text-green-900 disabled:text-gray-400 disabled:cursor-not-allowed underline"
                      title="Rebuild the title as 'Kathakala | Last1 | Last2 | Last3' from the selected storytellers."
                    >
                      ↻ Regenerate from storytellers
                    </button>
                  </div>
                  <input
                    type="text"
                    value={episodeForm.title}
                    onChange={(e) => setEpisodeForm({ ...episodeForm, title: e.target.value })}
                    placeholder="Leave blank to auto-fill on save · or type a template with {Storyteller-1}, {Storyteller-2}"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Episode Type</label>
                  <select
                    value={episodeForm.episode_type}
                    onChange={(e) => setEpisodeForm({ ...episodeForm, episode_type: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="weekly">Weekly Episode</option>
                    <option value="utsavam">Utsavam (Festival)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Scheduled Start (Central Time) *
                  </label>
                  <input
                    type="datetime-local"
                    value={episodeForm.scheduled_start}
                    onChange={(e) => setEpisodeForm({ ...episodeForm, scheduled_start: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Scheduled End (optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={episodeForm.scheduled_end}
                    onChange={(e) => setEpisodeForm({ ...episodeForm, scheduled_end: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* ---- Storyteller Picker ---- */}
            <div className="bg-white rounded-lg shadow-sm p-5">
              <h2 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">
                Storytellers ({selectedStorytellers.length} selected)
              </h2>

              {/* Selected storytellers - sortable list */}
              {selectedStorytellers.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-600 mb-2">Lineup Order</h3>
                  <div className="space-y-2">
                    {selectedStorytellers.map((st, idx) => (
                      <div key={st.storyteller_id}>
                        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                          <span className="text-sm font-bold text-green-700 w-6">{idx + 1}</span>
                          {st.photo_url ? (
                            <img src={st.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 text-xs">
                              No pic
                            </div>
                          )}
                          <span className="flex-1 font-medium text-sm">{st.name}</span>

                          {/* Upload photo if missing */}
                          {!st.photo_url && (
                            <label className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">
                              Upload photo
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  if (e.target.files[0]) handleUploadExistingPhoto(st.storyteller_id, e.target.files[0]);
                                }}
                              />
                            </label>
                          )}
                          {uploadingPhotoFor === st.storyteller_id && (
                            <span className="text-xs text-gray-400 animate-pulse">Uploading...</span>
                          )}

                          {/* Edit */}
                          <button
                            onClick={() =>
                              editingStorytellerId === st.storyteller_id
                                ? setEditingStorytellerId(null)
                                : handleOpenEditStoryteller(st.storyteller_id)
                            }
                            className={`text-xs px-2 py-1 rounded ${
                              editingStorytellerId === st.storyteller_id
                                ? "bg-blue-600 text-white"
                                : "bg-gray-100 text-gray-500 hover:bg-blue-100"
                            }`}
                          >
                            {editingStorytellerId === st.storyteller_id ? "Close" : "Edit"}
                          </button>

                          {/* Host badge */}
                          <button
                            onClick={() => setHostStorytellerId(hostStorytellerId === st.storyteller_id ? "" : st.storyteller_id)}
                            className={`text-xs px-2 py-1 rounded ${
                              hostStorytellerId === st.storyteller_id
                                ? "bg-purple-600 text-white"
                                : "bg-gray-100 text-gray-500 hover:bg-purple-100"
                            }`}
                            title="Set as host"
                          >
                            {hostStorytellerId === st.storyteller_id ? "Host" : "Set Host"}
                          </button>

                          {/* Reorder */}
                          <button onClick={() => moveStoryteller(idx, -1)} disabled={idx === 0}
                            className="text-gray-400 hover:text-gray-700 disabled:opacity-30">↑</button>
                          <button onClick={() => moveStoryteller(idx, 1)} disabled={idx === selectedStorytellers.length - 1}
                            className="text-gray-400 hover:text-gray-700 disabled:opacity-30">↓</button>

                          {/* Remove */}
                          <button
                            onClick={() => toggleStoryteller({ storyteller_id: st.storyteller_id })}
                            className="text-red-400 hover:text-red-600 text-lg"
                          >✕</button>
                        </div>

                        {/* Inline edit form */}
                        {editingStorytellerId === st.storyteller_id && (
                          <div className="border border-blue-300 bg-blue-50 rounded-lg p-3 mt-1 mb-1 ml-8">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Telugu Name *</label>
                                <input
                                  type="text"
                                  value={editStoryteller.storyteller_telugu}
                                  onChange={(e) => setEditStoryteller({ ...editStoryteller, storyteller_telugu: e.target.value })}
                                  className="w-full border rounded px-2 py-1.5 text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">English Name</label>
                                <input
                                  type="text"
                                  value={editStoryteller.storyteller_english}
                                  onChange={(e) => setEditStoryteller({ ...editStoryteller, storyteller_english: e.target.value })}
                                  className="w-full border rounded px-2 py-1.5 text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Gender</label>
                                <select
                                  value={editStoryteller.gender}
                                  onChange={(e) => setEditStoryteller({ ...editStoryteller, gender: e.target.value })}
                                  className="w-full border rounded px-2 py-1.5 text-sm"
                                >
                                  <option value="Male">Male</option>
                                  <option value="Female">Female</option>
                                </select>
                              </div>
                            </div>
                            <div className="mt-2">
                              <label className="block text-xs text-gray-600 mb-1">Introduction</label>
                              <textarea
                                value={editStoryteller.introduction}
                                onChange={(e) => setEditStoryteller({ ...editStoryteller, introduction: e.target.value })}
                                className="w-full border rounded px-2 py-1.5 text-sm"
                                rows={3}
                                placeholder="కథకుల పరిచయం..."
                              />
                            </div>
                            <div className="mt-2 flex items-center gap-3">
                              <label className="text-xs text-gray-600">
                                {st.photo_url ? "Replace photo:" : "Upload photo:"}
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="ml-2 text-xs"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    setEditPhotoFile(file);
                                    if (file) setEditPhotoPreview(URL.createObjectURL(file));
                                  }}
                                />
                              </label>
                              {editPhotoPreview && (
                                <img src={editPhotoPreview} alt="preview" className="w-10 h-10 rounded-full object-cover" />
                              )}
                              <div className="flex-1" />
                              <button
                                onClick={handleSaveEditStoryteller}
                                disabled={isSavingStoryteller}
                                className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
                              >
                                {isSavingStoryteller ? "Saving..." : "Save Changes"}
                              </button>
                              <button
                                onClick={() => { setEditingStorytellerId(null); setEditPhotoFile(null); setEditPhotoPreview(null); }}
                                className="text-gray-500 hover:text-gray-700 text-sm"
                              >Cancel</button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Search & pick */}
              <div className="border rounded-lg p-3 bg-gray-50">
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={storytellerSearch}
                    onChange={(e) => setStorytellerSearch(e.target.value)}
                    placeholder="Search by Telugu or English name..."
                    className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-green-500"
                  />
                  <button
                    onClick={() => setShowAddNew(!showAddNew)}
                    className="bg-green-700 text-white text-sm px-4 py-2 rounded hover:bg-green-800 whitespace-nowrap"
                  >
                    + Add New
                  </button>
                </div>

                {/* Add new storyteller inline form */}
                {showAddNew && (
                  <div className="border border-green-300 bg-green-50 rounded-lg p-3 mb-3">
                    <h4 className="text-sm font-semibold text-green-800 mb-2">New Storyteller</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Telugu Name *</label>
                        <input
                          type="text"
                          value={newStoryteller.storyteller_telugu}
                          onChange={(e) => setNewStoryteller({ ...newStoryteller, storyteller_telugu: e.target.value })}
                          className="w-full border rounded px-2 py-1.5 text-sm"
                          placeholder="తెలుగు పేరు"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">English Name</label>
                        <input
                          type="text"
                          value={newStoryteller.storyteller_english}
                          onChange={(e) => setNewStoryteller({ ...newStoryteller, storyteller_english: e.target.value })}
                          className="w-full border rounded px-2 py-1.5 text-sm"
                          placeholder="English Name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Gender</label>
                        <select
                          value={newStoryteller.gender}
                          onChange={(e) => setNewStoryteller({ ...newStoryteller, gender: e.target.value })}
                          className="w-full border rounded px-2 py-1.5 text-sm"
                        >
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                      </div>
                    </div>
                    <div className="mt-2">
                      <label className="block text-xs text-gray-600 mb-1">Introduction (paste bio)</label>
                      <textarea
                        value={newStoryteller.introduction}
                        onChange={(e) => setNewStoryteller({ ...newStoryteller, introduction: e.target.value })}
                        className="w-full border rounded px-2 py-1.5 text-sm"
                        rows={3}
                        placeholder="కథకుల పరిచయం..."
                      />
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                      <label className="text-xs text-gray-600">
                        Photo:
                        <input
                          type="file"
                          accept="image/*"
                          className="ml-2 text-xs"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            setNewPhotoFile(file);
                            if (file) {
                              const url = URL.createObjectURL(file);
                              setNewPhotoPreview(url);
                            }
                          }}
                        />
                      </label>
                      {newPhotoPreview && (
                        <img src={newPhotoPreview} alt="preview" className="w-10 h-10 rounded-full object-cover" />
                      )}
                      <div className="flex-1" />
                      <button
                        onClick={handleAddNewStoryteller}
                        disabled={isAddingStoryteller}
                        className="bg-green-700 text-white text-sm px-4 py-1.5 rounded hover:bg-green-800 disabled:opacity-50"
                      >
                        {isAddingStoryteller ? "Adding..." : "Add Storyteller"}
                      </button>
                      <button
                        onClick={() => { setShowAddNew(false); setNewPhotoFile(null); setNewPhotoPreview(null); }}
                        className="text-gray-500 hover:text-gray-700 text-sm"
                      >Cancel</button>
                    </div>
                  </div>
                )}

                {/* Storyteller list */}
                <div className="max-h-60 overflow-y-auto">
                  {filteredStorytellers.map((st) => {
                    const isSelected = selectedStorytellers.some((s) => s.storyteller_id === st.storyteller_id);
                    return (
                      <div
                        key={st.storyteller_id}
                        onClick={() => toggleStoryteller(st)}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer rounded hover:bg-gray-100 ${
                          isSelected ? "bg-green-100 border-l-4 border-green-600" : ""
                        }`}
                      >
                        {st.photo_url ? (
                          <img src={st.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 text-xs">?</div>
                        )}
                        <span className="text-sm font-medium">{st.storyteller_telugu}</span>
                        <span className="text-xs text-gray-400">{st.storyteller_english}</span>
                        {st.is_host && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 rounded">Host</span>}
                        <span className="flex-1" />
                        {isSelected && <span className="text-green-600 font-bold text-lg">✓</span>}
                      </div>
                    );
                  })}
                  {filteredStorytellers.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-4">No storytellers found. Try a different search or add a new one.</p>
                  )}
                </div>
              </div>
            </div>

            {/* ---- Poster (Google Slides) ---- */}
            <div className="bg-white rounded-lg shadow-sm p-5">
              <h2 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">
                Poster — Google Slides
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                Template placeholders: {"{{P1}}"}, {"{{P2}}"}, {"{{P3}}"} (photos), {"{{N1}}"}, {"{{N2}}"}, {"{{N3}}"} (names), {"{{HOST_PHOTO}}"}, {"{{HOST_NAME}}"}, {"{{DATE_CST}}"}, {"{{DATE_IST}}"}. The poster generator populates slide 1 of the deck.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Google Slides Deck ID</label>
                <input
                  type="text"
                  value={slidesDeckId}
                  onChange={(e) => {
                    let val = e.target.value;
                    // Extract deck ID from full Google Slides URL
                    const urlMatch = val.match(/\/presentation\/d\/([a-zA-Z0-9_-]+)/);
                    if (urlMatch) val = urlMatch[1];
                    setSlidesDeckId(val);
                  }}
                  placeholder="Paste deck URL or ID, e.g., 1JwRaTmZq5g8Wm7ZBFbUvT..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-green-500"
                />
                {slidesDeckId && (
                  <a
                    href={`https://docs.google.com/presentation/d/${slidesDeckId}/edit`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-blue-600 hover:underline mt-1 inline-block"
                  >
                    Open deck in Google Slides
                  </a>
                )}
              </div>

              {/* Theme picker — drives the Gemini backdrop (Phase 3) */}
              {editingEpisodeId && slidesDeckId && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Poster theme
                    <span className="text-xs text-gray-400 ml-2 font-normal">
                      Applied when you click Generate Poster
                    </span>
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {POSTER_THEMES.map((theme) => {
                      const selected = theme.id === posterThemeId;
                      return (
                        <button
                          key={theme.id}
                          type="button"
                          onClick={() => setPosterThemeId(theme.id)}
                          className={`text-left p-3 rounded-lg border-2 transition-all bg-white ${
                            selected
                              ? "border-green-700 ring-2 ring-green-200 shadow"
                              : "border-gray-200 hover:border-gray-400"
                          }`}
                        >
                          <div className="flex gap-1 mb-2 rounded overflow-hidden h-8">
                            {theme.palette.map((c, i) => (
                              <div
                                key={i}
                                className="flex-1"
                                style={{ backgroundColor: c }}
                              />
                            ))}
                          </div>
                          <div className="text-sm font-semibold text-gray-800">
                            {theme.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {theme.tagline}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Generate poster actions */}
              {editingEpisodeId && slidesDeckId && (
                <div className="bg-gray-50 border rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <button
                      onClick={() => handleGeneratePoster(false)}
                      disabled={isGeneratingPoster || posterServerOnline === false}
                      className="bg-green-700 text-white font-semibold px-6 py-2.5 rounded-lg hover:bg-green-800 disabled:opacity-50 shadow"
                    >
                      {isGeneratingPoster ? "Generating..." : "Generate Poster"}
                    </button>
                    <button
                      onClick={() => handleGeneratePoster(true)}
                      disabled={isGeneratingPoster}
                      className="bg-gray-200 text-gray-700 text-sm px-4 py-2 rounded hover:bg-gray-300 disabled:opacity-50"
                    >
                      Reset Slide
                    </button>
                    <span className={`text-xs px-2 py-1 rounded ${
                      posterServerOnline === true ? "bg-green-100 text-green-700" :
                      posterServerOnline === false ? "bg-red-100 text-red-700" :
                      "bg-gray-100 text-gray-500"
                    }`}>
                      {posterServerOnline === true ? "Lambda online" :
                       posterServerOnline === false ? "Lambda unreachable" : "Checking..."}
                    </span>
                  </div>
                  {posterServerOnline === false && (
                    <div className="text-xs text-gray-500">
                      Couldn't reach the deployed poster Lambda. Check network / VPN.
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mt-2">
                    <strong>Generate Poster</strong> — Nano Banana Pro creates the themed poster, the
                    template slide is duplicated, backdrop set as its background, photos/names/times
                    populated. Single call end-to-end; ~40 seconds. <strong>Reset Slide</strong>
                    clears placeholders (fast).
                  </p>

                  {/* In-progress spinner */}
                  {isGeneratingPoster && (
                    <div className="mt-3 p-3 rounded border bg-blue-50 border-blue-300 text-sm flex items-center gap-2">
                      <span className="animate-pulse">●</span>
                      <span>Generating — Gemini + Drive + Slides, ~40s…</span>
                      <button
                        type="button"
                        onClick={handleCheckPosterJobNow}
                        className="ml-auto text-xs underline text-blue-700 hover:text-blue-900"
                        title="Re-read the poster_jobs row in case Realtime / polling missed an update."
                      >
                        Check now
                      </button>
                    </div>
                  )}

                  {/* Last completed result */}
                  {!isGeneratingPoster && lastPosterResult && (
                    <div className="mt-3 p-3 rounded border bg-green-50 border-green-300 text-sm">
                      <div className="font-semibold mb-1">Poster generated ✓</div>
                      <div className="text-xs text-gray-700">{lastPosterResult.message}</div>
                      {lastPosterResult.deckUrl && (
                        <a href={lastPosterResult.deckUrl} target="_blank" rel="noreferrer"
                           className="text-xs text-blue-700 underline">Open deck →</a>
                      )}
                    </div>
                  )}

                  {/* ---- Schedule on YouTube ---- */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-3 mb-1">
                      <button
                        onClick={handleScheduleYouTube}
                        disabled={isSchedulingYouTube}
                        className="bg-red-600 text-white font-semibold px-6 py-2.5 rounded-lg hover:bg-red-700 disabled:opacity-50 shadow"
                      >
                        {isSchedulingYouTube ? "Scheduling…" : "Schedule YouTube Broadcast"}
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Creates a YouTube Live broadcast at the scheduled time, sets the thumbnail from slide 1 of the deck,
                      and adds the video to the KathaKala playlist. Title + description are built from the storyteller data.
                    </p>
                  </div>

                  {/* Local-CLI fallback — always works, independent of Lambda. */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-gray-600">
                        Or run locally (always works):
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const cmd = `GEMINI_API_KEY=$(aws secretsmanager get-secret-value --secret-id kathakala/google-oauth --query SecretString --output text --region us-east-1 | jq -r .gemini_api_key) python3 pipeline/generate_kathakala_poster.py --episode-id ${editingEpisodeId} --theme ${posterThemeId} --deck-id ${slidesDeckId}`;
                          navigator.clipboard.writeText(cmd);
                          setSuccessMsg("CLI command copied to clipboard.");
                          setTimeout(() => setSuccessMsg(""), 3000);
                        }}
                        className="text-xs text-green-700 hover:text-green-900 underline"
                      >
                        Copy command
                      </button>
                    </div>
                    <code className="block bg-white border rounded px-2 py-1.5 font-mono text-[11px] text-gray-700 select-all whitespace-pre-wrap break-all">
                      {`python3 pipeline/generate_kathakala_poster.py --episode-id ${editingEpisodeId || "<id>"} --theme ${posterThemeId} --deck-id ${slidesDeckId || "<deck>"}`}
                    </code>
                  </div>
                </div>
              )}
            </div>

            {/* ---- Action Buttons ---- */}
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleSaveEpisode}
                disabled={isSaving}
                className="bg-green-700 text-white font-semibold px-8 py-3 rounded-lg hover:bg-green-800 disabled:opacity-50 shadow-lg"
              >
                {isSaving ? "Saving..." : editingEpisodeId ? "Update Episode" : "Save Episode"}
              </button>
              {editingEpisodeId && (
                <button
                  onClick={resetForm}
                  className="bg-gray-200 text-gray-700 font-semibold px-6 py-3 rounded-lg hover:bg-gray-300"
                >
                  Cancel Edit
                </button>
              )}
              <button
                onClick={resetForm}
                className="bg-gray-100 text-gray-500 px-6 py-3 rounded-lg hover:bg-gray-200"
              >
                Reset
              </button>
            </div>
          </div>
        )}

        {/* ==================== EPISODES LIST TAB ==================== */}
        {activeTab === "episodes" && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b bg-gray-50 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">
                All Episodes ({episodes.length})
              </h2>
              <button
                onClick={() => { resetForm(); setActiveTab("create"); }}
                className="bg-green-700 text-white text-sm px-4 py-2 rounded hover:bg-green-800"
              >
                + New Episode
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-3 py-2 border-b">ID</th>
                    <th className="px-3 py-2 border-b">Title</th>
                    <th className="px-3 py-2 border-b">Type</th>
                    <th className="px-3 py-2 border-b">Scheduled</th>
                    <th className="px-3 py-2 border-b">Status</th>
                    <th className="px-3 py-2 border-b">Video</th>
                    <th className="px-3 py-2 border-b">Poster</th>
                    <th className="px-3 py-2 border-b">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {episodes.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-8 text-gray-400">No episodes yet.</td></tr>
                  )}
                  {episodes.map((ep) => {
                    const statusColors = {
                      draft: "bg-gray-100 text-gray-600",
                      poster_generated: "bg-blue-100 text-blue-700",
                      youtube_scheduled: "bg-purple-100 text-purple-700",
                      ready: "bg-green-100 text-green-700",
                      live: "bg-red-100 text-red-700",
                      completed: "bg-gray-200 text-gray-500",
                    };
                    return (
                      <tr key={ep.episode_id} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-500">{ep.episode_id}</td>
                        <td className="px-3 py-2 font-medium">{ep.title || "--"}</td>
                        <td className="px-3 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            ep.episode_type === "weekly" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"
                          }`}>
                            {ep.episode_type}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {ep.scheduled_start
                            ? new Date(ep.scheduled_start).toLocaleString("en-US", {
                                month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
                              })
                            : ep.publish_date || "--"}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded font-medium ${statusColors[ep.status] || "bg-gray-100 text-gray-500"}`}>
                            {ep.status || "—"}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {ep.video_id ? (
                            <a href={`https://youtube.com/watch?v=${ep.video_id}`} target="_blank" rel="noreferrer"
                              className="text-blue-600 hover:underline">{ep.video_id}</a>
                          ) : "--"}
                        </td>
                        <td className="px-3 py-2">
                          {ep.poster_image_url ? (
                            <a href={ep.poster_image_url} target="_blank" rel="noreferrer"
                              className="text-blue-600 hover:underline text-xs">View</a>
                          ) : "--"}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => handleEditEpisode(ep)}
                            className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                          >Edit</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default EpisodeCreator;
